import { useState } from 'react'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material'
import * as Yup from 'yup'
import Button from '@mui/material/Button'
import { useFormik } from 'formik'
import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'
import { message } from 'antd'
import { axiosInstance } from '../../../../../../../hooks/useAxios'
import CircularLoading from '../../../../../../../components/CircularProgress'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import { ONLY_DATE_TIME_FORMAT } from '../../../../../../../constants/global'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../../../constants/errors'

interface LeadCreateTrackingNotificationModalProps {
  isOpen: boolean;
  onClose: (visible: boolean) => void;
  onUpdateTrackingNotificationListSuccess: () => void;
  leadObjectId: string;
}

interface ILeadTrackingNotificationFormModal {
  date: Dayjs | null
  note: string
}

const getPipelineFormValidationSchema = () => {
  return Yup.object({
    date: Yup.string().required('กรุณาเลือกวันที่'),
    note: Yup.string(),
  })
}

export const LeadCreateTrackingNotificationModal = (props: LeadCreateTrackingNotificationModalProps) => {
  const { isOpen, onClose, onUpdateTrackingNotificationListSuccess, leadObjectId } = props
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const formik = useFormik<ILeadTrackingNotificationFormModal>({
    initialValues: {
      date: dayjs(),
      note: '',
    } as ILeadTrackingNotificationFormModal,
    validationSchema: getPipelineFormValidationSchema(),
    onSubmit: async (values: ILeadTrackingNotificationFormModal) => {
      if (!values.date) {
        message.error('กรุณาเลือกวันที่')
        setIsLoading(false)
        return
      }
      setIsLoading(true)
      try {
        const date = dayjs(values.date).format(ONLY_DATE_TIME_FORMAT)
        const formattedDate = date?.replace(
          /(\d{2})\/(\d{2})\/(\d{4}) (\d{2}:\d{2})/,
          "$3-$2-$1T$4"
        );
        await axiosInstance.post(`/lead/create-tracking-notification/${leadObjectId}`, {
          ...values,
          date: new Date(formattedDate),
        })
        message.success('เพิ่ม tracking notification สำเร็จ')
        formik.resetForm()
        onClose(false)
        onUpdateTrackingNotificationListSuccess()
      } catch (e: any) {
        console.error(e)
        const messageError = ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] || 'บันทึกข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
      setIsLoading(false)
    },
  })

  return (
    <Dialog open={isOpen} onClose={() => onClose(false)} maxWidth="md" fullWidth={true}>
      <CircularLoading isLoading={isLoading} />
      <DialogTitle>เพิ่ม tracking notification</DialogTitle>
      <DialogContent>
        <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12} lg={12}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  format={ONLY_DATE_TIME_FORMAT}
                  ampm={false}
                  label="เลือกวันที่ติดตาม"
                  name="date"
                  onChange={(e) => {
                    formik.setFieldValue('date', e)
                  }}
                  sx={{ width: '100%' }}
                  value={formik.values.date ? dayjs(formik.values.date) : null}
                  slotProps={{
                    textField: {
                      helperText: "",
                      error: false,
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="note"
                fullWidth
                label="หมายเหตุ"
                name="note"
                helperText={
                  formik.touched.note && formik.errors.note as string
                }
                error={
                  !!(formik.touched.note && formik.errors.note)
                }
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.note}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={2} justifyContent={'flex-end'}>
            <Grid item>
              <Button
                startIcon={<SaveIcon />}
                color="primary"
                type="submit"
                size="large"
                variant="contained"
              >
                เพิ่ม tracking notification
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="inherit"
                size="large"
                variant="contained"
                onClick={() => onClose(false)}
                startIcon={<ClearIcon />}
              >
                ปิดหน้าต่าง
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default LeadCreateTrackingNotificationModal
